<template>
  <auth-guard
    route
  >
    <auth-guard
      can="api.view_hedge,api.view_lot"
    >
      <brokered-settlement-skeleton
        v-if="loading"
        title="Internal Proforma"
      />
      <ValidationObserver
        v-else
        v-slot="{ invalid, reset }"
      >
        <v-form
          :readonly="isLocked"
          :value="invalid"
          class="mb-16"
        >
          <v-container>
            <url-breadcrumbs
              v-if="breadcrumbs"
              :items="breadcrumbs"
            />
            <h1>
              Internal Proforma
            </h1>
            <div class="caption">
              Analyzes and reports on the internal profitability of a lot after smelter processing, ensuring accurate tracking of company financial performance.
            </div>
            <v-toolbar
              flat
              dense
              color="transparent"
            >
              <v-btn
                :color="isLocked ? 'error' : ''"
                small
                rounded
                @click="isLocked = !isLocked"
              >
                {{ isLocked ? 'Locked' : 'unlocked' }}
                <v-icon
                  small
                  class="ml-2"
                >
                  {{ isLocked ? 'mdi-lock-outline' : 'mdi-lock-open-outline' }}
                </v-icon>
              </v-btn>
              <v-spacer />
              <info-tooltip>
                <template v-slot:wrap-me>
                  <v-btn
                    :disabled="isLocked"
                    text
                    @click="refreshData"
                  >
                    Refresh Data
                  </v-btn>
                </template>
                Replace values with the latest lot data.
              </info-tooltip>
              <!--            <info-tooltip>-->
              <!--              <template v-slot:wrap-me>-->
              <!--                <v-btn-->
              <!--                  :disabled="history.length === 0"-->
              <!--                  text-->
              <!--                  target="_blank"-->
              <!--                  :to="{ name: 'internal-report-print', params: { lot: $route.params.id } }"-->
              <!--                >-->
              <!--                  Internal Report-->
              <!--                </v-btn>-->
              <!--              </template>-->
              <!--              Replace values with the latest lot data.-->
              <!--            </info-tooltip>-->
              <v-btn
                :disabled="!history.length"
                text
                class="ml-2"
                target="_blank"
                :to="{ name: 'internal-proforma-print', params: { id: $route.params.id } }"
              >
                Print
              </v-btn>
            </v-toolbar>
            <v-select
              :value="historyVersion"
              label="Revision History"
              :items="history"
              :item-text="historyLabelFormat"
              item-value="id"
              @change="setVersionFromHistoryById"
            />
            <div>
              <h2>
                Properties
              </h2>
              <v-sheet class="pa-4">
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      v-model="lotName"
                      readonly
                      class="dense-filled"
                      label="Lot Name"
                      validation-rules="required|max:400"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <ValidationProvider
                      immediate
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-select
                        label="Yard"
                        v-model="yardId"
                        :items="$store.getters['internalProformaStore/getYardChoices']"
                        :item-text="x => `${x.id} - ${x.name} ${x.address || ''}, ${x.city || ''} ${x.state || ''}`"
                        item-value="id"
                        :error-messages="errors"
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-date
                      label="Est. Ship Date"
                      v-model="shipDate"
                      :show-date-picker="!isLocked"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      validation-rules="required|max_value:2147483647|min_value:0|decimal:0"
                      v-model="convertersProcessed"
                      label="Converters Processed"
                      type="number"
                      hint="Manual Count w/ fallback of Check In total"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      validation-rules="required|max_value:999999999|min_value:0|decimal:3"
                      v-model="mixedCatalystWeight"
                      label="Mixed Catalyst Weight/Received Lbs"
                      type="number"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-date
                      label="Metal Value Date"
                      v-model="metalValueDate"
                      :show-date-picker="!isLocked"
                    />
                  </v-col>
                </v-row>
              </v-sheet>
              <div class="py-4" />
              <h2>
                Metal Allocations
              </h2>
              <v-alert
                v-if="overAllocatedMetals.length"
                dense
                type="warning"
                class="my-3"
              >
                <div
                  v-for="item in overAllocatedMetals"
                  :key="item.metal"
                >
                  <strong>Over-allocation:</strong> {{ item.allocated }} of {{ item.available }} rtoz for {{ item.metal }} (over by {{ item.over }} rtoz)
                </div>
              </v-alert>
              <v-sheet class="pa-4">
                <span class="h2">Expected Return Values for Lot</span>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      v-model="availablePt"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Pt"
                    />
                    <v-progress-linear
                      height="24"
                      :value="allocatedPt / availablePt * 100"
                      color="#0068b9"
                      rounded
                    >
                      <span class="font-weight-medium white--text">
                        {{ allocatedPt }} / {{ availablePt }} rtoz
                      </span>
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      v-model="availablePd"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Pd"
                    />
                    <v-progress-linear
                      height="24"
                      :value="allocatedPd / availablePd * 100"
                      color="#d00e00"
                      rounded
                    >
                      <span class="font-weight-medium white--text">
                        {{ allocatedPd }} / {{ availablePd }} rtoz
                      </span>
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      v-model="availableRh"
                      readonly
                      type="number"
                      class="dense-filled"
                      validation-rules="required|max_value:99999999|min_value:0|decimal:3"
                      label="Available Rh"
                    />
                    <v-progress-linear
                      height="24"
                      :value="allocatedRh / availableRh * 100"
                      color="#006104"
                      rounded
                    >
                      <span class="font-weight-medium white--text">
                        {{ allocatedRh }} / {{ availableRh }} rtoz
                      </span>
                    </v-progress-linear>
                  </v-col>
                </v-row>
                <div
                  v-for="allocation in allocations"
                  :key="allocation.hedgeId"
                >
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-h6">
                      Hedge #{{ allocation.hedgeId }}
                    </div>
                    <v-btn
                      :disabled="isLocked"
                      outlined
                      x-small
                      @click="removeAllocation(allocation)"
                    >
                      Remove
                    </v-btn>
                  </div>
                  <v-row>
                    <v-col>
                      <internal-proforma-metal
                        alternate-style
                        :hedge="allocation"
                        @setHedge="updateAllocations(allocation.hedgeId, $event)"
                      />
                    </v-col>
                  </v-row>
                </div>
                <v-alert
                  v-if="overAllocatedMetals.length"
                  dense
                  type="warning"
                  class="mt-4 mb-2"
                >
                  <div
                    v-for="item in overAllocatedMetals"
                    :key="item.metal"
                  >
                    <strong>Over-allocation:</strong> {{ item.allocated }} of {{ item.available }} rtoz for {{ item.metal }} (over by {{ item.over }} rtoz)
                  </div>
                </v-alert>
                <div class="mt-8">
                  <div class="text-h6">
                    Add Hedges
                  </div>
                  <div class="d-flex align-center">
                    <search-autocomplete
                      v-model="selectedHedge"
                      class="mx-4"
                      label="Search for Hedges"
                      item-text="friendlyName"
                      api-path="/api/hedges/"
                      :params="{ ordering: '-id' }"
                      :format-entry="formatHedgeEntry"
                    />
                    <v-btn
                      :disabled="!selectedHedge || isLocked"
                      class="mr-4"
                      color="primary"
                      outlined
                      x-small
                      @click="addSearchedHedge(selectedHedge)"
                    >
                      Add
                    </v-btn>
                  </div>
                  <v-list>
                    <v-list-item
                      v-for="hedge in unallocatedHedges"
                      :key="hedge.id"
                    >
                      <v-list-item-content>
                        {{
                          `Hedge #${hedge.id} ${hedge.ptAvailableToz}@${hedge.ptRate} / ${hedge.pdAvailableToz}@${hedge.pdRate} / ${hedge.rhAvailableToz}@${hedge.rhRate}`
                        }}
                        <span class="text-caption">designated</span>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          :disabled="isLocked"
                          outlined
                          x-small
                          @click="addHedge(hedge)"
                        >
                          Add
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <div>
                      <v-divider />
                      <v-list-item>
                        <v-list-item-content>
                          Add All Designated Hedges
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            :disabled="isLocked"
                            color="accent"
                            outlined
                            x-small
                            @click="addAllHedges()"
                          >
                            Add All
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-list-item v-if="unallocatedHedges.length && allocations.length === 0">
                        <v-list-item-content>
                          Add Hedges from Client Proforma
                          <span class="text-caption">Includes allocated amounts</span>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            :disabled="isLocked"
                            color="accent"
                            outlined
                            x-small
                            @click="addClientProformaHedges()"
                          >
                            Add All
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </div>
                    <v-divider />
                    <v-list-item>
                      <v-list-item-content>
                        Empty Hedge
                        <span class="text-caption">custom</span>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          :disabled="isLocked"
                          outlined
                          x-small
                          @click="addBlankHedge"
                        >
                          Add
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="allocatedReturnablePt"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Pt rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      :value="allocatedReturnablePd"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Pd rtoz"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <rules-text
                      immediate
                      :value="allocatedReturnableRh"
                      class="dense-filled"
                      readonly
                      type="number"
                      label="Total Returnable Rh rtoz"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    offset-md="9"
                    md="3"
                  >
                    <rules-text
                      class="dense-filled"
                      :value="grossMetalValue"
                      label="Gross Metal Value"
                      prepend-icon="mdi-currency-usd"
                      hint="Sum Hedge Values"
                    />
                  </v-col>
                </v-row>
              </v-sheet>
            </div>
            <div class="py-4" />
            <h2>
              Fees
            </h2>
            <v-sheet class="pa-4">
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="refiningFee"
                    type="number"
                    label="Refining Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="processingFee"
                    type="number"
                    label="Processing Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="handlingFee"
                    type="number"
                    label="Handling Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="freightCharge"
                    type="number"
                    label="Freight Charge"
                    hint="Users total from allocated transits."
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="assayFee"
                    type="number"
                    label="Assay Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="shreddingFee"
                    type="number"
                    label="Shredding Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="brokerageFee"
                    type="number"
                    label="Brokerage Fee"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    v-model="netOtherFees"
                    type="number"
                    label="Net Other Fees"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-checkbox
                    class="px-4"
                    v-model="consolidateFees"
                    label="Consolidate Fees When Printing"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="totalFees"
                    readonly
                    class="dense-filled"
                    type="number"
                    label="Total Fees"
                    prepend-icon="mdi-currency-usd"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    :value="netValue"
                    readonly
                    class="dense-filled"
                    validation-rules="required|max_value:99999999|min_value:0|decimal:2"
                    type="number"
                    label="Net Value"
                    prepend-icon="mdi-currency-usd"
                    hint="Unpaid Value + Metal Value - Fees"
                  />
                </v-col>
              </v-row>
            </v-sheet>
            <div class="py-4" />
            <h2>
              Finance / Amount Due
            </h2>
            <v-sheet class="pa-4">
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-date
                    v-model="expectedPaymentDate"
                    label="Expected Payment Date"
                    prepend-icon="mdi-calendar"
                    :show-date-picker="!isLocked"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    readonly
                    class="dense-filled"
                    v-model="daysOfInterest"
                    label="Days of Interest"
                    type="number"
                    hint="Client Value Date - Expected Payment Date"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="interestPercent"
                    label="Interest Rate %"
                    type="number"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <rules-text
                    v-model="financeCharge"
                    class="dense-filled"
                    readonly
                    label="Finance Charge"
                    type="number"
                    hint="Net Value * Interest Percent / 360 * Days of Interest"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  offset-md="9"
                  md="3"
                >
                  <rules-text
                    :value="settlementAmountDue"
                    readonly
                    class="dense-filled"
                    label="Settlement Amount Due"
                    type="number"
                    hint="Net Value + Finance Charge"
                  />
                </v-col>
              </v-row>
            </v-sheet>
          </v-container>
          <text-fab
            v-if="!isLocked"
            bottom
            right
            fixed
            icon-text="mdi-content-save-outline"
            :color="isModified ? 'primary': ''"
            @click="save"
          >
            Save Changes
          </text-fab>
        </v-form>
      </ValidationObserver>
    </auth-guard>
  </auth-guard>
</template>

<script>

import BrokeredSettlementSkeleton from '@/views/Lots/components/Brokered/components/BrokeredSettlementSkeleton.vue'
import RulesText from '@/components/ValidationFields/RulesText.vue'
import RulesDate from '@/components/ValidationFields/RulesDate.vue'
import moment from 'moment'
import SearchAutocomplete from '@/base/components/SearchAutocomplete.vue'
import { mapGetters } from 'vuex'
import InternalProformaMetal from '@/views/Lots/InternalProforma/InternalProformaMetal.vue'

export default {
  name: 'InternalProforma',
  components: {
    InternalProformaMetal,
    SearchAutocomplete,
    RulesDate,
    RulesText,
    BrokeredSettlementSkeleton
  },
  data () {
    return {
      isModified: false,
      selectedHedge: null,
      breadcrumbs: [
        ['lot list', { name: 'lot-list' }],
        [`Lot #${this.$route.params.id}`, { name: 'lot' }],
        [`Internal Proforma`, { name: 'internal-proforma' }]
      ],
      yardChoices: [],
      loading: false,
      readonly: true
    }
  },
  watch: {
    snapshot () {
      this.isModified = true
    }
  },
  computed: {
    snapshot () {
      return [
        this.$store.getters['internalProformaStore/getStateSnapshot']
      ]
    },
    overAllocatedMetals () {
      const metals = [
        { name: 'Pt', allocated: +this.allocatedPt || 0, available: +this.availablePt || 0 },
        { name: 'Pd', allocated: +this.allocatedPd || 0, available: +this.availablePd || 0 },
        { name: 'Rh', allocated: +this.allocatedRh || 0, available: +this.availableRh || 0 }
      ]
      return metals
        .filter(m => +m.allocated.toFixed(3) > +m.available.toFixed(3))
        .map(m => ({
          metal: m.name,
          allocated: +m.allocated.toFixed(3),
          available: +m.available.toFixed(3),
          over: +(m.allocated - m.available).toFixed(3)
        }))
    },
    isLocked: {
      get () {
        return this.$store.getters['internalProformaStore/getIsLocked']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setIsLocked', value)
      }
    },
    yardId: {
      get () {
        return this.$store.getters['internalProformaStore/getYardId']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setYardId', value)
      }
    },
    shipDate: {
      get () {
        return this.$store.getters['internalProformaStore/getShipDate']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setShipDate', value)
      }
    },
    convertersProcessed: {
      get () {
        return this.$store.getters['internalProformaStore/getConvertersProcessed']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setConvertersProcessed', value)
      }
    },
    mixedCatalystWeight: {
      get () {
        return this.$store.getters['internalProformaStore/getMixedCatalystWeight']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setMixedCatalystWeight', value)
      }
    },
    metalValueDate: {
      get () {
        return this.$store.getters['internalProformaStore/getMetalValueDate']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setMetalValueDate', value)
      }
    },
    assayFee: {
      get () {
        return this.$store.getters['internalProformaStore/getAssayFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setAssayFee', value)
      }
    },
    shreddingFee: {
      get () {
        return this.$store.getters['internalProformaStore/getShreddingFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setShreddingFee', value)
      }
    },
    brokerageFee: {
      get () {
        return this.$store.getters['internalProformaStore/getBrokerageFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setBrokerageFee', value)
      }
    },
    refiningFee: {
      get () {
        return this.$store.getters['internalProformaStore/getRefiningFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setRefiningFee', value)
      }
    },
    processingFee: {
      get () {
        return this.$store.getters['internalProformaStore/getProcessingFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setProcessingFee', value)
      }
    },
    handlingFee: {
      get () {
        return this.$store.getters['internalProformaStore/getHandlingFee']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setHandlingFee', value)
      }
    },
    freightCharge: {
      get () {
        return this.$store.getters['internalProformaStore/getFreightCharge']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setFreightCharge', value)
      }
    },
    netOtherFees: {
      get () {
        return this.$store.getters['internalProformaStore/getNetOtherFees']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setNetOtherFees', value)
      }
    },
    consolidateFees: {
      get () {
        return this.$store.getters['internalProformaStore/getConsolidateFees']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setConsolidateFees', value)
      }
    },
    expectedPaymentDate: {
      get () {
        return this.$store.getters['internalProformaStore/getExpectedPaymentDate']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setExpectedPaymentDate', value)
      }
    },
    interestPercent: {
      get () {
        return this.$store.getters['internalProformaStore/getInterestPercent']
      },
      set (value) {
        this.$store.commit('internalProformaStore/setInterestPercent', value)
      }
    },
    allocations () {
      return this.$store.getters['internalProformaStore/getAllocations'].map(allocation => ({
        hedgeId: allocation.hedgeId,
        pt_returnable: allocation.ptToz,
        pt_return_rate: allocation.ptRate,
        pt_client_returnable: allocation.ptRtoz,
        pt_mk: allocation.ptMarketRate,
        pt_value: allocation.ptValue,
        pd_returnable: allocation.pdToz,
        pd_return_rate: allocation.pdRate,
        pd_client_returnable: allocation.pdRtoz,
        pd_mk: allocation.pdMarketRate,
        pd_value: allocation.pdValue,
        rh_returnable: allocation.rhToz,
        rh_return_rate: allocation.rhRate,
        rh_client_returnable: allocation.rhRtoz,
        rh_mk: allocation.rhMarketRate,
        rh_value: allocation.rhValue
      }))
    },
    ...mapGetters('internalProformaStore', {
      termsRefiningFeeTotal: 'getTermsRefiningFeeTotal',
      termsTreatmentCharge: 'getTermsTreatmentCharge',
      termsHandlingFee: 'getTermsHandlingFee',
      allocatedPd: 'getAllocatedPd',
      allocatedPt: 'getAllocatedPt',
      allocatedRh: 'getAllocatedRh',
      allocatedReturnablePd: 'getAllocatedReturnablePd',
      allocatedReturnablePt: 'getAllocatedReturnablePt',
      allocatedReturnableRh: 'getAllocatedReturnableRh',
      availablePd: 'getAvailablePd',
      availablePt: 'getAvailablePt',
      availableRh: 'getAvailableRh',
      daysOfInterest: 'getDaysOfInterest',
      financeCharge: 'getFinanceCharge',
      grossMetalValue: 'getGrossMetalValue',
      history: 'getAdvanceCalculatorHistory',
      historyVersion: 'getHistoryVersion',
      lotId: 'getLotId',
      lotName: 'getLotName',
      netValue: 'getNetValue',
      settlementAmountDue: 'getSettlementAmountDue',
      totalFees: 'getTotalFees',
      unallocatedHedges: 'getUnallocatedHedges',
      unallocatedPd: 'getUnallocatedPd',
      unallocatedPt: 'getUnallocatedPt',
      unallocatedRh: 'getUnallocatedRh'
    })
  },
  methods: {
    formatHedgeEntry (x) {
      const pt = x.pt_initial_toz ?? x.pt_available_toz
      const pd = x.pd_initial_toz ?? x.pd_available_toz
      const rh = x.rh_initial_toz ?? x.rh_available_toz
      return Object.assign({}, x, {
        friendlyName: `Hedge #${x.id} ${pt}@$${x.alternate_pt_rate_per_toz_fx} / ${pd}@$${x.alternate_pd_rate_per_toz_fx} / ${rh}@$${x.alternate_rh_rate_per_toz_fx}`
      })
    },
    historyLabelFormat (history) {
      let text = `${history.id}. ${moment(history.created_at).format('MM/DD/YYYY HH:mm:ss')} ${history.fx_currency}`
      if (this.history.length > 0 && history.id === this.history[0].id) text += ' (Latest)'
      return text
    },
    addSearchedHedge (hedge) {
      this.addHedge({
        id: hedge.id,
        ptAvailableToz: hedge.pt_initial_toz ?? hedge.pt_available_toz,
        ptRate: hedge.alternate_pt_rate_per_toz_fx,
        pdAvailableToz: hedge.pd_initial_toz ?? hedge.pd_available_toz,
        pdRate: hedge.alternate_pd_rate_per_toz_fx,
        rhAvailableToz: hedge.rh_initial_toz ?? hedge.rh_available_toz,
        rhRate: hedge.alternate_rh_rate_per_toz_fx
      })
    },
    addBlankHedge () {
      function getNextCSequence (ids) {
        let cValue = 0
        for (const id of ids) {
          if (!(typeof id === 'string') || !id.startsWith('C')) continue
          const x = parseInt(id.slice(1))
          if (isNaN(x) || x < cValue) continue
          cValue = x
        }
        // Return the next C sequence value up to the target sequence (e.g., "C5")
        return 'C' + (cValue + 1)
      }

      this.addHedge({
        id: getNextCSequence(this.allocations.map(x => x.hedgeId)),
        ptAvailableToz: this.unallocatedPt,
        ptRate: 0,
        pdAvailableToz: this.unallocatedPd,
        pdRate: 0,
        rhAvailableToz: this.unallocatedRh,
        rhRate: 0
      })
    },
    addHedge (hedge) {
      if (this.allocations.find(alloc => alloc.hedgeId === hedge.id)) {
        this.$store.commit('setSnackbarWarning', 'Allocation for Hedge already exists.')
        return
      }
      const addHedge = {
        hedgeId: hedge.id,
        ptRtoz: +(hedge.ptAvailableToz ?? 0),
        ptMarketRate: hedge.ptRate,
        pdRtoz: +(hedge.pdAvailableToz ?? 0),
        pdMarketRate: hedge.pdRate,
        rhRtoz: +(hedge.rhAvailableToz ?? 0),
        rhMarketRate: hedge.rhRate
      }
      this.$store.commit('internalProformaStore/addHedgeToAllocations', addHedge)
      this.isModified = true
    },
    addAllHedges () {
      this.unallocatedHedges.forEach(this.addHedge)
    },
    async addClientProformaHedges () {
      this.loading = true
      try {
        const count = await this.$store.dispatch('internalProformaStore/importClientProformaAllocations')
        if (count > 0) {
          this.isModified = true
          this.$store.commit('setSnackbarSuccess', `Imported ${count} hedge allocation(s) from Client Proforma.`)
        } else {
          this.$store.commit('setSnackbarWarning', 'No Client Proforma allocations found for this lot.')
        }
      } catch (err) {
        this.$store.commit('setSnackbarError', 'Failed to import Client Proforma allocations.')
      } finally {
        this.loading = false
      }
    },
    updateAllocations (hedgeId, allocation) {
      let updatedAllocations = []
      for (const line of this.$store.getters['internalProformaStore/getAllocations']) {
        if (line.hedgeId === hedgeId) {
          // line.ptToz = allocation.pt_returnable
          // line.ptRate = allocation.pt_return_rate
          line.ptRtoz = allocation.pt_client_returnable
          line.ptMarketRate = allocation.pt_mk
          line.ptValue = allocation.pt_value
          // line.pdToz = allocation.pd_returnable
          // line.pdRate = allocation.pd_return_rate
          line.pdRtoz = allocation.pd_client_returnable
          line.pdMarketRate = allocation.pd_mk
          line.pdValue = allocation.pd_value
          // line.rhToz = allocation.rh_returnable
          // line.rhRate = allocation.rh_return_rate
          line.rhRtoz = allocation.rh_client_returnable
          line.rhMarketRate = allocation.rh_mk
          line.rhValue = allocation.rh_value
        }
        updatedAllocations.push(line)
      }
      this.$store.commit('internalProformaStore/setAllocations', updatedAllocations)
    },
    removeAllocation (allocation) {
      this.isModified = true
      this.$store.commit('internalProformaStore/removeAllocation', allocation.hedgeId)
    },
    save () {
      this.$store.dispatch('internalProformaStore/saveAdvanceCalculator')
      this.isModified = false
    },
    async initialize () {
      return Promise.all([
        this.$store.dispatch('internalProformaStore/fetchAdvanceCalculators'),
        this.$store.dispatch('internalProformaStore/fetchDesignatedHedges')
      ])
    },
    setVersionFromHistoryById (id) {
      const history = this.history.find(h => h.id === id)
      if (history) {
        this.$store.commit('internalProformaStore/setContextFromHistory', history)
        return true
      }
      return false
    },
    refreshData () {
      return this.$store.dispatch('internalProformaStore/refreshDataFromLotData')
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.isModified) {
      if (!window.confirm('Leave without saving?')) {
        return
      }
    }
    next()
  },
  /*
   * Fetch the history, if no history then fetch the latest lot seed data
   */
  async mounted () {
    this.loading = true
    this.$store.commit('internalProformaStore/clearState')
    this.$store.commit('internalProformaStore/setLotId', this.$route.params.id)
    await this.initialize()
    if (this.history.length) {
      this.$store.commit('internalProformaStore/setContextFromHistory', this.history[0])
    } else {
      // if there is no history, then seed data
      await this.refreshData()
      if (!this.allocations.length && this.unallocatedHedges.length) {
        this.addAllHedges()
      }
      this.isLocked = false
    }
    this.loading = false
  }
}
</script>
